import axios from "axios";
import { joinURLs } from "../utils/urlUtil";
import { getHeadersConfig } from "../utils/config";
import { ErrorData, PublisherOrSiteOption, PublisherRecord, PublisherSiteRecord, ResponseStatus, TrafficSourceResponseDto } from "../utils/types";
import ErrorHandler from "../utils/ErrorHandler";
import StateHelper from "../utils/StateHelper";

function removeParensFromName(name: string) {
  // temporary check for the pub:siteID being present
  // until the API is updated accordingly
  if ( name.indexOf("(") >= 0 ) {
    name = name.replaceAll(/\s*[(][^)]+[)]\s*/g, " ")    
  }
  return name;
}
/**
 * Lower-case and trim the text, and add a duplicate copy that has all spaces removed.
 * So a search for "mobileposse" can match the publisher name "Mobile Posse"
 */
function prepTextForSearch(text: string) {
  text = text.toLowerCase();
  return text + " " + text.replaceAll(/ +/g, "");
}

export const TrafficSources = {
  getTrafficSourcesClinkables: async (abortController: AbortController, setClinkableTrafficSources:(clinkableTrafficSources:PublisherOrSiteOption[]) => void, usePublisherDisabled?:boolean) => {
    const trafficSources = await TrafficSources.getTrafficSources(abortController);

    const clinkableTrfficSources:PublisherOrSiteOption[] = trafficSources.filter((trafficSource:PublisherOrSiteOption) => {
      const publisher:PublisherRecord = (trafficSource.record as PublisherRecord);
      const pubSite:PublisherSiteRecord = (trafficSource.record as PublisherSiteRecord);
    
      if (pubSite && pubSite.apptapSiteId){
        return pubSite.clinkable === "true";
      } else {
        publisher.publisherSites = publisher.publisherSites.filter((publisherSite:PublisherSiteRecord) => {
          return publisherSite.clinkable === "true";
        })
        return publisher.publisherSites.length;
      }
    });

    // Create a deep copy to avoid modifying the original cached data
    const resultTrafficSources = clinkableTrfficSources.map(trafficSource => ({
      ...trafficSource,
      record: { ...trafficSource.record }
    }));

    // Apply publisher disabled flag after filtering, only to the copy
    if (usePublisherDisabled) {
      resultTrafficSources.forEach((trafficSource:PublisherOrSiteOption) => {
        const pubSite:PublisherSiteRecord = (trafficSource.record as PublisherSiteRecord);
        if (!pubSite || !pubSite.apptapSiteId) {
          // This is a publisher (group level), not a site
          trafficSource.disabled = true;
        }
      });
    }

    setClinkableTrafficSources(resultTrafficSources);
  },

  getTrafficSources: async (abortController: AbortController, setTrafficSourceResponseDto?:(trafficSourceResponseDto:TrafficSourceResponseDto) => void) => {
    const trafficSourcesDtoState:TrafficSourceResponseDto = StateHelper.getTrafficSourcesDtoState();
    if (trafficSourcesDtoState && trafficSourcesDtoState.publishersArray && trafficSourcesDtoState.publishersArray.length) {
      if (setTrafficSourceResponseDto) setTrafficSourceResponseDto(trafficSourcesDtoState);
      return trafficSourcesDtoState.publishersArray;
    }
    StateHelper.setTrafficSourcesDtoState({
      status: ResponseStatus.LOADING, 
      error: null,
      publishersArray: []
    });

    try {
      const url = joinURLs("2/dashboard/publishers", process.env.REACT_APP_REPORTING_API_BASE_URL);
      const response:any = await axios.get(url, {...getHeadersConfig(), signal: abortController.signal });
      const pubResponseArray: PublisherRecord[] = response.data.publishers;

      // map publishers to format used by the auto-complete
      let pubArray: (PublisherOrSiteOption)[] = [];

      for (let i = 0; i < pubResponseArray.length; i++) {
        const publisher = pubResponseArray[i];
        let pubOption: PublisherOrSiteOption = Object();
        publisher.apptapPublisherName = removeParensFromName(publisher.apptapPublisherName);
        pubOption.record = publisher
        pubOption.key = publisher.apptapPublisherId;
        pubOption.id = publisher.apptapPublisherId;
        pubOption.text = [
          pubOption.key,
          prepTextForSearch(publisher.apptapPublisherName)
        ].join(" ");
        pubArray.push(pubOption);

        publisher.publisherSites.sort((a,b) => a.apptapPublisherSiteName.localeCompare(b.apptapPublisherSiteName)).forEach( site => {
          let siteOption: PublisherOrSiteOption = Object();
          siteOption.record = site
          siteOption.key = site.apptapPublisherId + ":" + site.apptapSiteId;
          siteOption.id = site.apptapPublisherId + ":" + site.apptapSiteId;
          siteOption.text = [
            siteOption.key,
            prepTextForSearch(publisher.apptapPublisherName),
            prepTextForSearch(site.apptapPublisherSiteName)
          ].join(" ");            

          site.apptapPublisherName = publisher.apptapPublisherName;
          site.apptapPublisherSiteName = removeParensFromName(site.apptapPublisherSiteName);

          // make the publisher option have all of the sites' text for easy search logic
          // so this will keep getting added to until it has both the top-level publisher data
          // and all sites' data
          pubOption.text = [
            pubOption.text,
            siteOption.key,
            prepTextForSearch(site.apptapPublisherSiteName)
          ].join(" ");

          pubArray.push(siteOption);
        });
      }

      const dto: TrafficSourceResponseDto = {
        status : ResponseStatus.OK, 
        error : null,
        publishersArray : pubArray
      };

      if (setTrafficSourceResponseDto) setTrafficSourceResponseDto(dto);
      StateHelper.setTrafficSourcesDtoState(dto);

      return pubArray;
    } catch (error:any) {
      const isLoggedOut = ErrorHandler.handleAuthError(error);
      if (!isLoggedOut) {
        const errData : ErrorData = ErrorHandler.getErrorMessage(error);
        const dto : TrafficSourceResponseDto = {
          status : ResponseStatus.ERROR, 
          error : errData,
          publishersArray : []
        };

        if (setTrafficSourceResponseDto) setTrafficSourceResponseDto(dto);
        StateHelper.setTrafficSourcesDtoState(dto);
      }
      return [];
    }
  }
}